import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  ModalResult,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { MatTableDataSource } from '@angular/material/table';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { FreeRepairDetail } from '../../models/free-repair-detail.model';
import { UpdateFreeRepair } from '../../models/update-free-repair.model';
import { v4 as uuidv4 } from 'uuid';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';

@Component({
  selector: 'app-free-repair-setup',
  templateUrl: './free-repair-setup.component.html',
  styleUrl: './free-repair-setup.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FreeRepairSetupComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<FreeRepairDetail>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  @Input() freeRepairId: string;

  private fb = inject(FormBuilder);
  public translationService = inject(TranslationService);
  private productDataService = inject(ProductDataService);
  private featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  isCreateMode = false;
  code: string;
  description: string;
  totalRepairs: number;
  isCopyAs: boolean;
  freeRepairsSetupFormGroup: FormGroup;

  freeRepairDetails: FreeRepairDetail[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  saveEnabled = false;
  isModified = false;
  isCreateInProgress = false;

  // Initialize Data source for table
  dataSource: MatTableDataSource<FreeRepairDetail>;

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {
      type: { cell: (row) => row.repairType, header: this.translationService.translate('typeColumnText') },
      qty: { cell: 'qty', header: this.translationService.translate('quantityText') }
    }
  };

  constructor() {
    this.freeRepairsSetupFormGroup = this.fb.group({
      code: '',
      description: '',
      totalRepairs: ''
    });
  }

  ngOnInit(): void {
    this.freeRepairId = this.route.snapshot.paramMap.get('freeRepairId');
    // Determine if we are in create or edit mode based on presence of freeRepairId
    this.isCreateMode = !this.freeRepairId;
    const { code, description, totalRepairs, isCopyAs } = history.state;
    this.code = code;
    this.description = description;
    this.totalRepairs = totalRepairs;
    this.isCopyAs = isCopyAs;
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }

    // Patch values into the form so they show in the input fields
    this.freeRepairsSetupFormGroup.patchValue({
      code: this.code,
      description: this.description,
      totalRepairs: this.totalRepairs
    });
    if (!this.isCreateMode && !this.isCopyAs) {
      // Disable code input in edit mode
      this.freeRepairsSetupFormGroup.get('code')?.disable();
    }

    if (this.isCreateMode || this.isCopyAs) {
      this.isModified = true;
    } else {
      this.freeRepairsSetupFormGroup.valueChanges.subscribe(() => {
        this.isModified = this.checkIfModified();
      });
      this.displayedRowsForm.valueChanges.subscribe(() => {
        this.isModified = this.checkIfModified();
      });
    }

    if (this.isCreateMode) {
      // new free repair create
      this.productDataService.getFreeRepairDetailsForCreate().subscribe((response: any) => {
        this.freeRepairDetails = response.data.freeRepairs || [];
        this.updateDisplayedRowsForm(this.freeRepairDetails);
        this.dataSource = new MatTableDataSource(this.freeRepairDetails);
        this.cdr.markForCheck();
      });
    } else {
      // free repair edit
      this.productDataService.getFreeRepairDetails(this.freeRepairId).subscribe((response: any) => {
        this.freeRepairDetails = response.data.freeRepairs || [];

        // Update instance variables with values from API response to establish baseline for change detection
        // This ensures checkIfModified() has correct original values when page is accessed directly via URL
        if (response.data.code !== undefined) {
          this.code = response.data.code;
          const codeControl = this.freeRepairsSetupFormGroup.get('code');
          const wasDisabled = codeControl?.disabled;
          if (wasDisabled) {
            codeControl?.enable({ emitEvent: false });
          }
          codeControl?.patchValue(response.data.code, { emitEvent: false });
          if (wasDisabled) {
            codeControl?.disable({ emitEvent: false });
          }
        }
        if (response.data.description !== undefined) {
          this.description = response.data.description;
          this.freeRepairsSetupFormGroup.get('description')?.patchValue(response.data.description, { emitEvent: false });
        }
        if (response.data.total !== undefined) {
          this.totalRepairs = response.data.total;
          this.freeRepairsSetupFormGroup.get('totalRepairs')?.patchValue(response.data.total, { emitEvent: false });
        }

        this.updateDisplayedRowsForm(this.freeRepairDetails);
        this.dataSource = new MatTableDataSource(this.freeRepairDetails);
        this.cdr.markForCheck();
      });
    }
  }

  createRowForm(row: FreeRepairDetail): FormGroup {
    return this.fb.group({
      type: [row.repairType],
      qty: [row.qty]
    });
  }

  updateDisplayedRowsForm(rows: any[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: FreeRepairDetail, controlName: string): FormControl {
    const index = this.freeRepairDetails.findIndex((row) => row.freeRepairsPk === item.freeRepairsPk); // Use appropriate unique key
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  cancel(): void {
    this.router.navigate(['/home/productData/freeRepair']);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const codeControl = this.freeRepairsSetupFormGroup.get('code');
    const noCodeInlineErrors = codeControl.valid && !codeControl.errors;
    const descriptionControl = this.freeRepairsSetupFormGroup.get('description');
    const noDescriptionInlineErrors = descriptionControl.valid && !descriptionControl.errors;
    const totalRepairsControl = this.freeRepairsSetupFormGroup.get('totalRepairs');
    const noTotalRepairsInlineErrors = totalRepairsControl.valid && !totalRepairsControl.errors;

    if (this.isCreateMode) {
      //Show error if code is empty or only whitespace
      if (!codeControl.value || codeControl.value.trim() === '') {
        codeControl.markAsTouched();
        codeControl.setErrors({ required: true });
        this.cdr.markForCheck();
      }
    }

    // Show error if description is empty or only whitespace
    if (!descriptionControl.value || descriptionControl.value.trim() === '') {
      descriptionControl.markAsTouched();
      descriptionControl.setErrors({ required: true });
      this.cdr.markForCheck();
    }

    // Show error if totalRepairs is empty or only whitespace
    if (!totalRepairsControl.value || totalRepairsControl.value.toString().trim() === '' || isNaN(totalRepairsControl.value)) {
      totalRepairsControl.markAsTouched();
      totalRepairsControl.setErrors({ invalid: true });
      this.cdr.markForCheck();
    }

    const code = codeControl.value;
    const description = descriptionControl.value;
    const totalRepairs = totalRepairsControl.value;
    if (
      !code ||
      !description ||
      !totalRepairs ||
      (this.isCreateMode && !noCodeInlineErrors) ||
      !noDescriptionInlineErrors ||
      !noTotalRepairsInlineErrors
    ) {
      return;
    }

    if (this.isSumOfSelectedTypesGreaterThanTotalRepairs()) {
      this.cdr.markForCheck();
      this.alertService.showAlert({
        type: AlertType.critical,
        content: this.translationService.translate('freeRepairGreaterThanTotalText')
      } as Alert);
      return;
    }

    this.isCreateInProgress = true;
    if (this.isCreateMode || this.isCopyAs) {
      console.log('Creating or copying free repair');
      // Create new free repair
      const modifiedRows = [];
      const formArray = this.displayedRowsForm.get('rows') as FormArray;
      for (let i = 0; i < this.freeRepairDetails.length; i++) {
        const original = this.freeRepairDetails[i];
        const current = formArray.at(i).value;
        if (this.isCopyAs) {
          // For copy-as, reset primary key to indicate new entry
          modifiedRows.push({
            ...current,
            qty: current.qty === null || current.qty === '' ? 0 : current.qty, // ensure qty is numeric and default to 0 when empty/null
            repairType: original.repairType,
            displayType: original.displayType,
            freeRepairsPk: uuidv4() // generate new UUID for copied entry
          });
        } else {
          modifiedRows.push({
            ...current,
            qty: current.qty === null || current.qty === '' ? 0 : current.qty, // ensure qty is numeric and default to 0 when empty/null
            repairType: original.repairType,
            displayType: original.displayType,
            freeRepairsPk: original.freeRepairsPk
          });
        }
      }
      const createFreeRepair: UpdateFreeRepair = {
        code: code,
        description: description,
        total: totalRepairs,
        freeRepairs: modifiedRows
      };
      this.productDataService.createFreeTableEntry(createFreeRepair).subscribe({
        next: () => {
          this.isCreateInProgress = false;
          this.cdr.markForCheck();
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
        },
        error: () => {
          this.isCreateInProgress = false;
          this.cdr.markForCheck();
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      // Update existing free repair
      const modifiedRows = [];
      const formArray = this.displayedRowsForm.get('rows') as FormArray;
      for (let i = 0; i < this.freeRepairDetails.length; i++) {
        const original = this.freeRepairDetails[i];
        const current = formArray.at(i).value;

        // Detect modification: compare actual values (before normalization)
        // This ensures we catch when user clears a field (e.g., "0" → "")
        if (original.repairType != current.type || original.qty != current.qty) {
          // Normalize for backend submission: convert empty/null to 0
          const normalizedQty = current.qty === null || current.qty === '' ? 0 : current.qty;
          modifiedRows.push({
            ...current,
            qty: normalizedQty,
            repairType: original.repairType,
            displayType: original.displayType,
            freeRepairsPk: original.freeRepairsPk
          });
        }
      }
      if (modifiedRows.length === 0) {
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('noModifiedText')
        } as Alert);
      } else {
        const payload: UpdateFreeRepair = {
          code: code,
          description: description,
          total: totalRepairs,
          freeRepairs: modifiedRows
        };
        this.productDataService.updateFreeRepairs(payload, this.freeRepairId).subscribe({
          next: () => {
            this.isCreateInProgress = false;
            this.isModified = false;
            this.cdr.markForCheck();
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('camSaveSuccessText')
            } as Alert);
          },
          error: () => {
            this.isCreateInProgress = false;
            this.cdr.markForCheck();
            this.alertService.showAlert({
              type: AlertType.critical,
              content: this.translationService.translate('criticalErrorAlertText')
            } as Alert);
          }
        });
      }
    }
  }

  checkValidTotalRepairs() {
    const totalRepairsControl = this.freeRepairsSetupFormGroup.get('totalRepairs');
    const totalRepairsValue = totalRepairsControl.value;
    // Only allow positive integers
    if (
      totalRepairsValue === null ||
      totalRepairsValue === '' ||
      isNaN(totalRepairsValue) ||
      Number(totalRepairsValue) < 0 ||
      !/^[0-9]+$/.test(totalRepairsValue.toString())
    ) {
      totalRepairsControl.setErrors({ invalid: true });
    } else {
      totalRepairsControl.setErrors(null);
    }
  }

  checkIfFreeRepairCodeExists() {
    const freeRepairCode = this.freeRepairsSetupFormGroup.get('code').value;
    if (freeRepairCode === this.code) {
      // unchanged from original code, no need to check
      this.freeRepairsSetupFormGroup.get('code').setErrors(null);
      return;
    }
    this.productDataService.freeRepairCodeExists(freeRepairCode).subscribe((exists) => {
      if (exists) {
        this.freeRepairsSetupFormGroup.get('code').setErrors({ exists: true });
      } else {
        this.freeRepairsSetupFormGroup.get('code').setErrors(null);
      }
    });
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.saveEnabled && this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.freeRepairDetails);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters({}, true);
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.saveEnabled && this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  /**
   * Returns true if any rows have been modified (type or qty changed) or if header fields (code, description, totalRepairs) have been modified.
   */
  checkIfModified(): boolean {
    // Check if header fields have been modified
    const codeControl = this.freeRepairsSetupFormGroup.get('code');
    const descriptionControl = this.freeRepairsSetupFormGroup.get('description');
    const totalRepairsControl = this.freeRepairsSetupFormGroup.get('totalRepairs');

    // Normalize values for comparison (treat null, undefined, "" as equivalent)
    const normalizeEmpty = (val: any) => (val === null || val === undefined || val === '' ? 'EMPTY' : val);

    const codeChanged = normalizeEmpty(codeControl?.value) !== normalizeEmpty(this.code);
    const descriptionChanged = normalizeEmpty(descriptionControl?.value) !== normalizeEmpty(this.description);
    const totalRepairsChanged = normalizeEmpty(totalRepairsControl?.value) !== normalizeEmpty(this.totalRepairs);

    if (codeChanged || descriptionChanged || totalRepairsChanged) {
      return true;
    }

    // Check if any table rows have been modified
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.freeRepairDetails.length; i++) {
      const original = this.freeRepairDetails[i];
      const current = formArray.at(i).value;

      // For row comparison, compare the actual values without normalization
      // This detects when user clears a field (e.g., removes "0" → "")
      if (original.repairType != current.type || original.qty != current.qty) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the sum of qty for rows with type in the specified list is greater than totalRepairs.
   */
  isSumOfSelectedTypesGreaterThanTotalRepairs(): boolean {
    const selectedTypes = ['NAIL', 'RNAIL', 'RNAIL CROWN', 'RNAIL SHOULDER', 'SECTION'];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    let sum = 0;
    for (let i = 0; i < this.freeRepairDetails.length; i++) {
      const row = this.freeRepairDetails[i];
      if (selectedTypes.includes(row.repairType)) {
        const qtyControl = formArray.at(i).get('qty');
        const qty = Number(qtyControl?.value) || 0;
        sum += qty;
      }
    }
    const totalRepairsValue = Number(this.freeRepairsSetupFormGroup.get('totalRepairs')?.value) || 0;
    return sum > totalRepairsValue;
  }

  assign(): void {
    // TODO: Implement assign logic here
    console.log('Assign button clicked');
    // You can add your assignment logic here, e.g., open a dialog or perform an action
  }

  preventNegativeInput(event: KeyboardEvent): void {
    // Prevent minus, 'e', and other non-numeric input
    if (event.key === '-' || event.key === 'Subtract' || event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '.') {
      event.preventDefault();
    }
  }

  enforceMaxLength(event: Event, maxLength: number): void {
    const input = event.target as HTMLInputElement;
    if (input && input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
      const control = this.freeRepairsSetupFormGroup.get('totalRepairs');
      if (control) {
        control.setValue(input.value, { emitEvent: false });
      }
    }
  }

  enableCodeControl() {
    // Enable code input in edit mode
    this.freeRepairsSetupFormGroup.get('code')?.enable();
  }
}
